<template>
  <el-dialog
      :model-value="modelValue"
      :title="isEdit ? 'Ma\'lumot turini tahrirlash' : 'Yangi ma\'lumot turi'"
      width="580px"
      align-center
      destroy-on-close
      @update:model-value="$emit('update:modelValue', $event)"
      @closed="langErrors = []"
  >
    <el-form ref="formRef" :model="form" :rules="rules" label-position="top">
      <div class="fc-label">
        Qator nomi <span class="fc-label__hint">(kamida o'zbekcha lotin)</span>
      </div>
      <el-tabs v-model="activeLang" class="fc-lang-tabs">
        <el-tab-pane v-for="lt in langTabs" :key="lt.code" :name="lt.code">
          <template #label>
            <span class="fc-lang-tab" :class="{'is-error': langErrors.includes(lt.code)}">
              {{ lt.tabLabel }}
              <span
                  class="fc-lang-tab__dot"
                  :class="{'is-filled': !!form.title[lt.code]}"
                  :title="form.title[lt.code] ? 'To\'ldirilgan' : 'Bo\'sh'"
              />
            </span>
          </template>
          <el-form-item :prop="'title.' + lt.code" label-width="0">
            <el-input
                v-model="form.title[lt.code]"
                :placeholder="'Qator nomi — ' + langLabels[lt.code]"
                clearable
                @input="clearLangError(lt.code)"
            />
          </el-form-item>
        </el-tab-pane>
      </el-tabs>

      <el-form-item label="Havola" prop="link_url">
        <el-input v-model="form.link_url" placeholder="https://openbudget.uz/state-budget/balance" clearable/>
        <div class="fc-hint">
          Qator bosilganda ochiladigan sahifa. Masalan:
          <code>https://openbudget.uz/state-budget/balance</code>
        </div>
      </el-form-item>

      <el-row :gutter="16">
        <el-col :span="12">
          <div class="fc-label">Tartib raqami</div>
          <el-input-number v-model="form.order_number" :min="0" controls-position="right" style="width: 100%"/>
          <div class="fc-hint">Jadvaldagi qatorlar tartibi. Yangi qatorga oxirgi raqamdan keyingisi qo'yiladi.</div>
        </el-col>
        <el-col :span="12">
          <div class="fc-label">Holati</div>
          <el-switch
              v-model="form.is_active"
              inline-prompt
              active-text="Faol"
              inactive-text="Nofaol"
              style="--el-switch-on-color: #16A34A"
          />
          <div class="fc-hint">Nofaol qilinsa qator portaldan butunlay yo'qoladi.</div>
        </el-col>
      </el-row>
    </el-form>

    <template #footer>
      <el-button plain :disabled="submitting" @click="$emit('update:modelValue', false)">
        Bekor qilish
      </el-button>
      <el-button type="primary" :loading="submitting" @click="onSubmit">
        {{ isEdit ? "Saqlash" : "Qo'shish" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage, ElNotification } from "element-plus";
import api, {
  LANG_TABS,
  LANG_LABELS,
  fillMultiLang,
  apiErrorMessage,
} from "./financeCalendarApi";

export default {
  name: "DatasetDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    dataset: { type: Object, default: null },
    // Yangi qator uchun taklif qilinadigan tartib raqami (ro'yxatdagi oxirgisi + 1).
    nextOrder: { type: Number, default: 1 },
  },
  emits: ["update:modelValue", "saved"],
  data() {
    return {
      submitting: false,
      activeLang: "uz_lat",
      langErrors: [],
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
      form: this.buildForm(null),
      rules: {
        "title.uz_lat": [{ required: true, message: "Majburiy maydon", trigger: "blur" }],
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.dataset;
    },
  },
  watch: {
    modelValue(val) {
      if (!val) return;
      this.activeLang = "uz_lat";
      this.langErrors = [];
      this.form = this.buildForm(this.dataset);
    },
  },
  methods: {
    buildForm(dataset) {
      return {
        title: fillMultiLang(dataset?.title),
        link_url: dataset?.link_url || "",
        order_number: dataset?.order_number ?? this.nextOrder,
        is_active: dataset?.is_active !== false,
      };
    },
    clearLangError(code) {
      this.langErrors = this.langErrors.filter((c) => c !== code);
    },
    onSubmit() {
      this.langErrors = [];
      this.$refs.formRef.validate((valid, invalidFields) => {
        if (!valid) {
          ElMessage.error("Iltimos, majburiy maydonlarni to'ldiring");
          if (invalidFields) {
            this.langErrors = Object.keys(invalidFields)
                .filter((key) => key.startsWith("title."))
                .map((key) => key.split(".")[1]);
            if (this.langErrors[0]) this.activeLang = this.langErrors[0];
          }
          return;
        }
        // PUT to'liq almashtirish — hamma maydon yuboriladi.
        const payload = {
          title: { ...this.form.title },
          link_url: this.form.link_url.trim(),
          order_number: Number(this.form.order_number) || 0,
          is_active: this.form.is_active,
        };
        this.submitting = true;
        const request = this.isEdit
            ? api.updateDataset(this.dataset.id, payload)
            : api.createDataset(payload);
        request
            .then(({ data }) => {
              ElMessage({
                type: "success",
                message: this.isEdit ? "Qator yangilandi" : "Qator yaratildi",
              });
              this.$emit("update:modelValue", false);
              this.$emit("saved", data?.id);
            })
            .catch((error) => {
              ElNotification.error({
                title: "Saqlashda xatolik",
                message: apiErrorMessage(error),
              });
            })
            .finally(() => {
              this.submitting = false;
            });
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./finance-calendar.scss";
</style>
